import type { catalog, designs, orders, admin, identity, integration } from '@besbello/contracts';
import type { z } from 'zod';

/**
 * CLIENTE DE API.
 *
 * Tipado desde `@besbello/contracts`: si cambia un contrato, esto deja de
 * compilar. Es justo lo que se quiere.
 *
 * Reglas:
 *  - `credentials: 'include'` SIEMPRE: la sesion es una cookie `HttpOnly`, no un
 *    token en `localStorage`.
 *  - Toda mutacion lleva el token CSRF.
 *  - Un error llega con CODIGO estable; la pantalla decide por el codigo, nunca
 *    por el texto.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { ...options.headers };

  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers['content-type'] = 'application/json';
  }
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'include',
    signal: options.signal,
    body:
      options.body === undefined
        ? undefined
        : options.body instanceof FormData
          ? options.body
          : JSON.stringify(options.body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: unknown } })?.error;
    throw new ApiError(
      error?.code ?? 'INTERNAL_ERROR',
      error?.message ?? `HTTP ${response.status}`,
      response.status,
      error?.details,
    );
  }

  return payload as T;
}

type Infer<T extends z.ZodTypeAny> = z.infer<T>;

export const api = {
  // ---------------------------------------------------------------- identidad
  me: () => request<Infer<typeof identity.MeResponse>>('/api/me'),

  ssoStart: (next?: string) =>
    request<{ authorizeUrl: string }>(`/api/auth/sso/start${next ? `?next=${encodeURIComponent(next)}` : ''}`, {
      headers: { accept: 'application/json' },
    }),

  staffLogin: (email: string, password: string, otp?: string) =>
    request<Infer<typeof identity.MeResponse>>('/api/auth/staff/login', {
      method: 'POST',
      body: { email, password, ...(otp ? { otp } : {}) },
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  setLocale: (locale: 'gl' | 'es' | 'en') =>
    request<{ ok: true }>('/api/me/locale', { method: 'PUT', body: { locale } }),

  // ----------------------------------------------------------------- catalogo
  listProducts: (params: { page?: number; pageSize?: number; family?: string; q?: string } = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined) search.set(key, String(value));
    return request<Infer<typeof catalog.ListProductsResponse>>(`/api/catalog/products?${search}`);
  },

  getProduct: (id: string, params: { size?: string; colorId?: string } = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined) search.set(key, String(value));
    return request<Infer<typeof catalog.ProductDetail>>(`/api/catalog/products/${id}?${search}`);
  },

  // ------------------------------------------------------------------- diseno
  createDesign: (body: Infer<typeof designs.CreateDesignBody>) =>
    request<Infer<typeof designs.DesignDto>>('/api/designs', { method: 'POST', body }),

  getDesign: (id: string) => request<Infer<typeof designs.DesignDto>>(`/api/designs/${id}`),

  saveDesignVersion: (id: string, body: Infer<typeof designs.SaveVersionBody>) =>
    request<Infer<typeof designs.SaveVersionResponse>>(`/api/designs/${id}/versions`, { method: 'PUT', body }),

  validateDesign: (id: string, body: { document?: unknown; size?: string }) =>
    request<Infer<typeof designs.ValidateDesignResponse>>(`/api/designs/${id}/validate`, { method: 'POST', body }),

  /**
   * Guarda dónde cae el estampado sobre la foto de esta vista.
   *
   * Es un dato del CATÁLOGO, no del diseño: afecta a todos los diseños de ese
   * producto. Por eso vive bajo /admin y lo pide una cuenta con permiso.
   */
  /**
   * Guarda dónde cae el estampado sobre la foto de esta vista.
   *
   * Es un dato del CATÁLOGO, no del diseño: afecta a todos los diseños de ese
   * producto. Por eso vive bajo /admin y lo pide una cuenta con permiso.
   */
  guardarEncajeMockup: (
    productId: string,
    viewCode: string,
    body: { anchor: { xFrac: number; yFrac: number }; mmPerPixel: number },
  ) =>
    request<{ viewCode: string; provenance: string; confidence: number }>(
      `/api/admin/products/${productId}/views/${encodeURIComponent(viewCode)}/mockup`,
      { method: 'PUT', body },
    ),

  /** Las zonas de esta prenda. Un diseño suelto se devuelve él solo. */
  zonasDelDiseno: (id: string) => request<Infer<typeof designs.GrupoDto>>(`/api/designs/${id}/zonas`),

  /** Añade otra zona a la misma prenda: pecho y espalda en el mismo pedido. */
  verSerie: (id: string) => request<Infer<typeof designs.SerieDto>>(`/api/designs/${id}/serie`),
  ponerSerie: (id: string, body: Infer<typeof designs.PonerSerieBody>) =>
    request<Infer<typeof designs.SerieDto>>(`/api/designs/${id}/serie`, { method: 'PUT', body }),

  anadirZona: (id: string, body: Infer<typeof designs.AnadirZonaBody>) =>
    request<Infer<typeof designs.DesignDto>>(`/api/designs/${id}/zonas`, { method: 'POST', body }),

  /** Los diseños empezados de este cliente, de lo último tocado a lo primero. */
  misDisenos: () => request<{ items: Array<Infer<typeof designs.DesignSummaryDto>> }>('/api/designs'),

  /** Enlace de solo lectura y caduco, para que lo aprueben antes de pagar. */
  compartirDiseno: (id: string, dias = 7) =>
    request<Infer<typeof designs.CompartirDisenoResponse>>(`/api/designs/${id}/compartir`, {
      method: 'POST',
      body: { dias },
    }),

  /** Lo que ve quien recibe el enlace. Sin sesión y de solo lectura. */
  verCompartido: (token: string) =>
    request<{
      productName: string;
      colorName: string;
      zoneId: string;
      viewCode: string;
      document: unknown;
      expiresAt: string;
    }>(`/api/compartido/${encodeURIComponent(token)}`),

  /**
   * Pedir otra vez.
   *
   * El snapshot congelado guarda el diseño exacto: repetirlo es leerlo, no
   * reconstruirlo. Devuelve un diseño NUEVO, para no tocar el del pedido.
   */
  repetirPedido: (orderId: string) =>
    request<{ designId: string; productId: string; colorId: string }>(`/api/orders/${orderId}/repetir`, {
      method: 'POST',
    }),

  /** Sesión de invitado: se puede diseñar, no se puede pagar. */
  entrarComoInvitado: () =>
    request<{ userId: string; invitado: boolean; deleteAt?: string }>('/api/auth/invitado', { method: 'POST' }),

  /**
   * Corrige la ZONA de marcaje de un producto: su forma y su orientación.
   *
   * Es un dato del catálogo, como el encaje: afecta a todos los diseños de ese
   * producto, y por eso lo pide una cuenta con permiso.
   */
  guardarZona: (
    productId: string,
    zoneId: string,
    body: { polygonMm: Array<{ x: number; y: number }>; viewCode: string | null; note?: string },
  ) =>
    request<{ id: string; provenance: string; confidence: number }>(
      `/api/admin/products/${productId}/zones/${encodeURIComponent(zoneId)}`,
      { method: 'PUT', body: { ...body, colorId: null, confirmed: true } },
    ),

  /** Cambia el color de la prenda del diseño. No toca el documento. */
  cambiarColorDiseno: (id: string, colorId: string) =>
    request<Infer<typeof designs.DesignDto>>(`/api/designs/${id}/color`, {
      method: 'PATCH',
      body: { colorId },
    }),

  uploadAsset: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<Infer<typeof designs.AssetDto>>('/api/assets', { method: 'POST', body: form });
  },

  /**
   * Quitar el fondo o vectorizar. Devuelve un asset NUEVO: el original se
   * conserva para que el cliente pueda arrepentirse.
   */
  tratarAsset: (checksum: string, body: Infer<typeof designs.TratarAssetBody>) =>
    request<Infer<typeof designs.TratarAssetResponse>>(
      `/api/assets/${encodeURIComponent(checksum)}/tratar`,
      { method: 'POST', body },
    ),

  /** Todo lo que este cliente ha subido, para reutilizarlo sin volver a buscarlo. */
  misImagenes: (designId: string) =>
    request<{ items: Array<Infer<typeof designs.AssetDto>> }>(`/api/designs/${designId}/assets`),

  // ------------------------------------------------------------------- precio
  createQuote: (body: Infer<typeof orders.QuoteBody>) =>
    request<Infer<typeof orders.QuoteResponse>>('/api/pricing/quote', { method: 'POST', body }),

  checkoutDecision: (quoteId: string) =>
    request<Infer<typeof orders.CheckoutDecisionResponse>>('/api/checkout/decision', {
      method: 'POST',
      body: { quoteId },
    }),

  walletBalance: () =>
    request<{ available: boolean; balance: Infer<typeof integration.WalletBalance> | null }>('/api/wallet/balance'),

  // ------------------------------------------------------------------ pedidos
  confirmOrder: (body: Infer<typeof orders.ConfirmOrderBody>, idempotencyKey: string) =>
    request<Infer<typeof orders.ConfirmOrderResponse>>('/api/orders/confirm', {
      method: 'POST',
      body,
      headers: { 'idempotency-key': idempotencyKey },
    }),

  listOrders: () => request<Infer<typeof orders.ListOrdersResponse>>('/api/orders'),
  getOrder: (id: string) => request<Infer<typeof orders.OrderDto>>(`/api/orders/${id}`),
  cancelOrder: (id: string) => request<Infer<typeof orders.OrderDto>>(`/api/orders/${id}/cancel`, { method: 'POST' }),

  // --------------------------------------------------------------------- RGPD
  listConsents: () => request<{ items: Array<{ kind: string; granted: boolean; at: string; policyVersion: string }> }>(
    '/api/rgpd/consents',
  ),
  grantConsent: (kind: string, granted: boolean, policyVersion: string) =>
    request<unknown>('/api/rgpd/consents', { method: 'POST', body: { kind, granted, policyVersion } }),
  exportMyData: () => request<unknown>('/api/rgpd/export'),
  createDsr: (type: string, detail?: string) =>
    request<unknown>('/api/rgpd/requests', { method: 'POST', body: { type, detail } }),

  // ------------------------------------------------------------------- admin
  adminKpi: () => request<Infer<typeof admin.CalibrationKpiResponse>>('/api/admin/calibration/kpi'),

  adminExceptions: (params: { page?: number; maxConfidence?: number; status?: string } = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined) search.set(key, String(value));
    return request<Infer<typeof admin.ListExceptionsResponse>>(`/api/admin/catalog/exceptions?${search}`);
  },

  resolveException: (id: string, note?: string) =>
    request<{ ok: true }>(`/api/admin/catalog/exceptions/${id}/resolve`, { method: 'POST', body: { note } }),

  effectiveCalibration: (sku: string, size?: string) =>
    request<Infer<typeof admin.EffectiveCalibrationResponse>>(
      `/api/admin/calibrations/${encodeURIComponent(sku)}/effective${size ? `?size=${size}` : ''}`,
    ),

  putZoneOverride: (productId: string, zoneId: string, body: Infer<typeof admin.PutZoneOverrideBody>) =>
    request<Infer<typeof admin.ZoneOverrideDto>>(
      `/api/admin/products/${productId}/zones/${encodeURIComponent(zoneId)}`,
      { method: 'PUT', body },
    ),

  adminSampling: () => request<{ items: Array<Infer<typeof admin.SamplingTaskDto>> }>('/api/admin/calibration/sampling'),

  reviewSampling: (id: string, verdict: 'correct' | 'incorrect', note?: string) =>
    request<{ ok: true }>(`/api/admin/calibration/sampling/${id}`, { method: 'POST', body: { verdict, note } }),

  scheduleSampling: (sampleRate?: number) =>
    request<{ scheduled: number }>('/api/admin/calibration/sampling/schedule', {
      method: 'POST',
      body: { sampleRate },
    }),

  importRoly: (body: { source: 'inline' | 'url'; payload?: string; url?: string; dryRun: boolean }) =>
    request<Infer<typeof admin.CatalogImportDto>>('/api/admin/catalog/imports/roly', { method: 'POST', body }),

  listImports: () => request<{ items: Array<Infer<typeof admin.CatalogImportDto>> }>('/api/admin/catalog/imports'),

  priceBooks: () => request<{ items: Array<Infer<typeof admin.PriceBookSummary>> }>('/api/admin/price-books'),
  priceBook: (id: string) => request<Record<string, unknown>>(`/api/admin/price-books/${id}`),
  putThreshold: (body: Infer<typeof admin.UpdateThresholdBody>) =>
    request<{ ok: true }>('/api/admin/checkout/threshold', { method: 'PUT', body }),

  workshopOrders: () => request<{ items: Array<Infer<typeof admin.WorkshopOrderDto>> }>('/api/admin/workshop/orders'),

  updateOrderStatus: (id: string, status: string, note?: string) =>
    request<Infer<typeof orders.OrderDto>>(`/api/admin/orders/${id}/status`, {
      method: 'POST',
      body: { status, note },
    }),

  health: () => request<{ status: string; checks: Record<string, string> }>('/api/health'),
};

export type Api = typeof api;
